import React, {
    useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import { ConfirmDialog, ToastRegion, useToast } from '../ui';
import useAdminConsole from '../../hooks/useAdminConsole';
import { useTenantOverview } from '../../hooks/useAdminForms';
import { countAll, flattenOrgs, sourcesToFix } from '../../utils/adminConsole';
import { accessSummary } from '../../utils/adminAccess';
import UsersSection, { NO_FILTERS } from './UsersSection';
import UserDrawer from './UserDrawer';
import { AccessSection, DealershipsSection } from './OrgSections';
import SourcesSection from './SourcesSection';
import SettingsSection from './SettingsSection';
import PresetsSection from './PresetsSection';
import { confirmLeave, hasLeaveGuard } from '../../utils/leaveGuard';
import {
    AddDealershipDialog, AddUserDialog, AttachSourceDialog, DeleteOrgDialog, OnboardDialog, OrgEditDialog, ResultDialog,
} from './dialogs';
import {
    DbIcon, LayoutIcon, LockIcon, OrgIcon, ShieldIcon, SlidersIcon, UsersIcon,
} from './icons';
import '../ui/ui.css';
import './AdminV2.css';

/**
 * The redesigned admin console (shown with the "new design" flag on, inside
 * the v2 app shell; the v2 top bar carries the scope, the org in view, Sign
 * out and leaving through the dealership picker, D18).
 *
 * All state and calls come from hooks/useAdminConsole and hooks/useAdminForms,
 * shared with today's console. Layout from the prototype: section nav + scope
 * card, Users (A2 quick filters, drawer), Dealerships (tree), Access (A1,
 * super-admin only, D19), Data sources (A4). Out (D21): Invited status, last
 * sign-in, activity log, staff postings, last refresh; Suspend source (dec. 8).
 */
function AdminConsoleV2({ me }) {
    const c = useAdminConsole({ me, trackRoot: true });
    const { isSuper } = c;
    const t = useTenantOverview({ enabled: isSuper });
    const tenants = useMemo(() => (isSuper ? t.tenants : []), [isSuper, t.tenants]);
    const { toast, showToast, dismissToast } = useToast();
    const toastText = useCallback((text) => showToast({ text }), [showToast]);

    const [section, setSection] = useState('users');
    const [filters, setFilters] = useState(NO_FILTERS);
    const [drawerId, setDrawerId] = useState(null);
    const [addedId, setAddedId] = useState(null);
    const mainRef = useRef(null);

    // Super-admins only: A1 needs the tenants (GET /tenants is super-only).
    // A fresh cache whenever the tree or the sources change.
    const summaries = useMemo(() => new Map(), [c.orgs, tenants]); // eslint-disable-line react-hooks/exhaustive-deps
    const summaryOf = useCallback((u) => {
        if (!isSuper) return { level: 'none', edit: [], ro: [], access: {} };
        const key = `${u.id}|${u.org_id}|${u.role}|${u.status}|${u.apps?.pricepath ? 1 : 0}`;
        if (!summaries.has(key)) summaries.set(key, accessSummary(u, c.orgs, tenants));
        return summaries.get(key);
    }, [isSuper, summaries, c.orgs, tenants]);

    // Unsaved settings edits ask first (QA P2-6). The new section starts at
    // the top of the page (QA L4); focusing the main area must not scroll.
    const show = (id) => {
        setSection(id);
        window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
        mainRef.current?.focus({ preventScroll: true });
    };
    const go = (id) => {
        if (id === section || !hasLeaveGuard()) { show(id); return; }
        confirmLeave().then((leave) => { if (leave) show(id); });
    };

    // The user in the drawer: the level in view first, else anyone (Access section).
    const drawerUser = drawerId
        ? (c.users.find((u) => u.id === drawerId) || c.rootUsers.find((u) => u.id === drawerId) || null)
        : null;
    useEffect(() => {
        // Removed (or gone after a reload): close the drawer.
        if (drawerId && !drawerUser && !c.usersLoading) setDrawerId(null);
    }, [drawerId, drawerUser, c.usersLoading]);
    const closeDrawer = () => { setDrawerId(null); c.setConfirmId(null); };

    const refresh = () => {
        c.refreshAll();
        if (isSuper) { c.refreshRoot(); t.load(); }
    };

    const onTeam = (org) => {
        setFilters(NO_FILTERS);
        if (isSuper) { c.setScope(org.id); c.setQuery(''); }
        go('users');
    };

    const dealerCount = countAll(c.orgs, 'COG');
    const toFix = isSuper && !t.loading ? sourcesToFix(t.tenants, c.orgs) : 0;
    const sections = isSuper
        ? [
            { id: 'users', label: 'Users', Icon: UsersIcon, n: c.rootUsers.length },
            { id: 'dealerships', label: 'Dealerships', Icon: OrgIcon, n: dealerCount },
            { id: 'access', label: 'Access', Icon: ShieldIcon },
            { id: 'sources', label: 'Data sources', Icon: DbIcon, n: toFix ? `${toFix} to fix` : null, warn: Boolean(toFix) },
            { id: 'presets', label: 'Role presets', Icon: LayoutIcon },
            { id: 'settings', label: 'Settings', Icon: SlidersIcon },
        ]
        : [
            { id: 'users', label: 'Team', Icon: UsersIcon, n: c.users.length },
            { id: 'dealerships', label: 'Dealership', Icon: OrgIcon, n: c.orgOptions.length },
            { id: 'presets', label: 'Role presets', Icon: LayoutIcon },
            { id: 'settings', label: 'Settings', Icon: SlidersIcon },
        ];
    const current = sections.some((s) => s.id === section) ? section : 'users';

    const resetUser = c.resetConfirm
        ? (c.users.find((u) => u.id === c.resetConfirm) || c.rootUsers.find((u) => u.id === c.resetConfirm))
        : null;
    const detachTenant = t.confirmId ? t.tenants.find((x) => x.id === t.confirmId) : null;
    const dealers = flattenOrgs(c.orgs).filter((o) => o.type === 'COG');

    return (
        <div className="ad2" data-testid="admin-v2">
            <div className="ad2-page">
                <div className="ad2-adm">
                    <aside className="ad2-snav" aria-label="Admin sections">
                        <div>
                            <div className="ad2-sn-h">{isSuper ? 'Fusable admin' : 'Team admin'}</div>
                            <ul>
                                {sections.map(({
                                    id, label, Icon, n, warn,
                                }) => (
                                    <li key={id}>
                                        <button type="button" aria-current={current === id ? 'page' : undefined} onClick={() => go(id)}>
                                            <Icon />
                                            <span>{label}</span>
                                            {n != null && !c.loading ? <span className={`ad2-sn-n${warn ? ' is-warn' : ''}`}>{n}</span> : <span />}
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <div className={`ad2-scope ad2-scope--${isSuper ? 'staff' : 'dealer'}`}>
                            {isSuper ? (
                                <>
                                    <span className="ad2-kind ad2-kind--staff">Fusable staff</span>
                                    <p><b>You administer every dealership.</b> Data sources and the access overview are yours alone.</p>
                                    <p>
                                        Without a dealership membership, staff open dealerships
                                        {' '}<span className="ad2-acc ad2-acc--ro"><LockIcon />Read-only</span>: look and diagnose, not create tasks.
                                    </p>
                                </>
                            ) : (
                                <>
                                    <span className="ad2-kind ad2-kind--dealer">Dealer admin</span>
                                    <p><b>You manage the {me?.org_name || 'dealership'} team.</b> You can add plain users. Admin roles are granted by Fusable.</p>
                                </>
                            )}
                        </div>
                    </aside>

                    <main className="ad2-main" ref={mainRef} tabIndex={-1} aria-label="Admin console">
                        {current === 'users' && (
                            <UsersSection
                                c={c}
                                me={me}
                                filters={filters}
                                setFilters={setFilters}
                                summaryOf={summaryOf}
                                onOpen={(u) => setDrawerId(u.id)}
                                onAdd={() => c.setShowAdd(true)}
                                onRefresh={refresh}
                                selectedId={drawerId}
                            />
                        )}
                        {current === 'dealerships' && (
                            <DealershipsSection c={c} users={c.rootUsers} tenants={tenants} onTeam={onTeam} onOnboard={() => c.setShowOnboard(true)} />
                        )}
                        {current === 'access' && isSuper && (
                            <AccessSection c={c} users={c.rootUsers} tenants={tenants} onOpen={(u) => setDrawerId(u.id)} />
                        )}
                        {current === 'sources' && isSuper && (
                            <SourcesSection
                                orgs={c.orgs}
                                t={{
                                    ...t,
                                    activate: (x) => t.activate(x).then((ok) => { if (ok) toastText(`${x.org_name || x.slug} is live`); }),
                                }}
                                onDetach={(x) => t.setConfirmId(x.id)}
                            />
                        )}
                        {current === 'presets' && (
                            <PresetsSection dealers={dealers} isSuper={isSuper} onToast={toastText} onUsersChanged={c.reloadUsers} />
                        )}
                        {current === 'settings' && (
                            <SettingsSection dealers={dealers} isSuper={isSuper} onToast={toastText} />
                        )}
                    </main>
                </div>
            </div>
            <footer className="ad2-appfoot"><span>&copy; 2026 Fusable. All rights reserved.</span></footer>

            {drawerUser && (
                <UserDrawer
                    user={drawerUser}
                    c={c}
                    me={me}
                    tenants={tenants}
                    summary={isSuper ? summaryOf(drawerUser) : null}
                    onClose={closeDrawer}
                    onToast={toastText}
                />
            )}

            {c.showAdd && (
                <AddUserDialog
                    orgs={c.orgs}
                    me={me}
                    tenants={tenants}
                    onClose={() => c.setShowAdd(false)}
                    onAdded={(res, { picked } = {}) => {
                        c.onUserAdded(res);
                        if (res?.user?.id) setAddedId(res.user.id);
                        if (!picked?.invite) toastText(`${picked?.display_name || 'User'} added`);
                    }}
                />
            )}
            {c.showOnboard && (
                <OnboardDialog
                    orgs={c.orgs}
                    onClose={() => c.setShowOnboard(false)}
                    onDone={(r) => {
                        c.onOnboarded(r);
                        if (isSuper) t.load();
                        toastText(`${r?.dealer?.name || 'Dealership'} created`);
                    }}
                />
            )}
            {c.editTarget && (
                <OrgEditDialog
                    org={c.editTarget}
                    orgs={c.orgs}
                    isSuper={isSuper}
                    onClose={() => c.setEditTarget(null)}
                    onSaved={() => { c.onOrgSaved(); toastText('Saved'); }}
                />
            )}
            {c.addChild && <AddDealershipDialog c={c} onAdded={toastText} />}
            {c.deleteTarget && <DeleteOrgDialog c={c} users={c.rootUsers} tenants={tenants} onDeleted={toastText} />}

            <ConfirmDialog
                open={Boolean(resetUser)}
                title="Reset password?"
                message={resetUser ? (
                    <>
                        We email <b>{resetUser.email}</b> a temporary password. Their current password stops working now,
                        and they must choose a new one at next sign-in. The temporary password is never shown here.
                        {c.actionError && <span className="ad2-alert ad2-alert--inline" role="alert">{c.actionError}</span>}
                    </>
                ) : ''}
                confirmLabel={c.busyId && resetUser && c.busyId === resetUser.id ? 'Resetting…' : 'Reset'}
                cancelLabel="No"
                busy={Boolean(resetUser && c.busyId === resetUser.id)}
                onConfirm={() => c.doResetPw(resetUser)}
                onCancel={() => { c.setResetConfirm(null); c.setActionError(null); }}
            />

            {c.resetResult && (
                <ResultDialog
                    result={c.resetResult}
                    onClose={() => { c.setResetResult(null); setAddedId(null); }}
                    onOpenProfile={addedId && c.users.some((u) => u.id === addedId) ? () => {
                        c.setResetResult(null);
                        setDrawerId(addedId);
                        setAddedId(null);
                    } : undefined}
                />
            )}

            {isSuper && t.attach && (
                <AttachSourceDialog
                    dealers={dealers}
                    tenants={t.tenants}
                    dealerId={t.attach.dealerId}
                    onClose={() => t.setAttach(null)}
                    onSaved={(row, { existing, orgId } = {}) => {
                        t.load();
                        const name = dealers.find((d) => d.id === orgId)?.name || 'Dealership';
                        toastText(`${name}: source ${existing ? 're-pointed' : 'connected'}. Run Check, then Activate.`);
                    }}
                />
            )}

            <ConfirmDialog
                open={Boolean(detachTenant)}
                title="Detach this source?"
                danger
                message={detachTenant ? (
                    <>
                        <b>{detachTenant.org_name || detachTenant.slug}</b> stops reading from <span className="ad2-ep">{detachTenant.synapse_server}</span>.
                        Its users keep their accounts but see no data until a source is attached again. The warehouse belongs to the
                        data team and is left as is.
                    </>
                ) : ''}
                confirmLabel="Detach"
                busy={Boolean(detachTenant && t.busyId === detachTenant.id)}
                onConfirm={() => {
                    const name = detachTenant.org_name || detachTenant.slug;
                    t.detach(detachTenant).then((ok) => { if (ok) toastText(`${name} source detached`); else t.setConfirmId(null); });
                }}
                onCancel={() => t.setConfirmId(null)}
            />

            <ToastRegion toast={toast} onDismiss={dismissToast} />
        </div>
    );
}

AdminConsoleV2.propTypes = {
    me: PropTypes.shape({
        is_admin: PropTypes.bool,
        is_super_admin: PropTypes.bool,
        org_name: PropTypes.string,
        email: PropTypes.string,
        display_name: PropTypes.string,
    }),
};

export default AdminConsoleV2;
